/**
 * Projections over feeding events: a lot's counted inventory (receipts, moves between pens, head
 * out) and a pen's feeding state (ration, last call, last delivery). Pure, so the facets, the
 * server's read models and the tests all use the same rules.
 */
import type { FacetEvent, Quantity } from "../../../contract";
import { headByPen, headOn, toLb, type LedgerEntry } from "./math";

export interface ReceiveData {
  head_received: number;
  head_expected?: number;
  pen_split?: Array<{ pen_id: string; head: number }>;
  pay_weight?: Quantity;
  pay_weight_total?: Quantity;
  gross_weight_total?: Quantity;
  shrink_pct?: number;
  arrival_weight?: Quantity;
  price?: { minor: number; currency: string };
  price_basis?: "per_cwt" | "per_head" | "total";
  owners?: Array<{ contact_id: string; share: number }>;
  origin?: string;
  ticket?: string;
  shortage_reason?: "short_shipped" | "dead_on_arrival" | "unknown";
  risk?: "low" | "medium" | "high";
  target_days?: number;
  process_on?: string;
  processing?: string;
  notes?: string;
}

export interface LotMoveData { head: number; notes?: string }

export interface HeadOutData {
  head: number;
  reason: "shipped" | "died" | "transferred" | "missing";
  pay_weight?: Quantity;
  pay_weight_total?: Quantity;
  price?: { minor: number; currency: string };
  price_basis?: "per_cwt" | "per_head" | "total";
  cause?: string;
  notes?: string;
}

export type BunkScore = "slick" | "crumbs" | "thin" | "half" | "heavy" | "untouched";

export interface CallData {
  bunk?: BunkScore;
  amount?: Quantity;
  per_head?: Quantity;
  change_per_head?: Quantity;
  ration?: string;
  ration_version_id?: string;
  head?: number;
  drops?: number;
  notes?: string;
}

export interface LoadData {
  load_id?: string;
  ration?: string;
  ration_version_id: string;
  batch?: Quantity;
  ingredients: Array<{ item_id: string; target?: Quantity; actual: Quantity }>;
  left_over?: Quantity;
  waste?: Quantity;
  weights_from?: "scale" | "manual";
  notes?: string;
}

export interface DeliveryData {
  amount: Quantity;
  ration?: string;
  ration_version_id?: string;
  load_id?: string;
  drop?: number;
  skip_reason?: "equipment" | "weather" | "pen_empty" | "other";
  notes?: string;
}

// ---------------------------------------------------------------- lot inventory

export interface LotReceipt {
  event_id: string;
  on: string;
  head: number;
  expected: number | null;
  pens: Record<string, number>;
  pay_lb_total: number | null;
  pay_avg_lb: number | null;
  seller_id: string | null;
  shortage_reason: string | null;
  identified: number;
}

export interface LotOut { event_id: string; on: string; head: number; reason: string; pen_id: string | null; pay_lb_total: number | null }
export interface LotMove { event_id: string; on: string; head: number; from: string | null; to: string | null }

export interface LotState {
  received: number;
  expected: number | null;
  short: number;
  in_on: string | null;
  receipts: LotReceipt[];
  outs: LotOut[];
  moves: LotMove[];
  ledger: LedgerEntry[];
  owners: Array<{ contact_id: string; share: number }>;
  /** Sum of owner shares when owners are listed. */
  owners_total: number | null;
  target_days: number | null;
  origin: string | null;
  /** Current head (through `today`) and where they are. */
  head: number;
  pens: Record<string, number>;
  out: { shipped: number; died: number; transferred: number; missing: number };
}

type LedgerEvent = Pick<FacetEvent, "id" | "type" | "occurred_at" | "data" | "subjects">;

const subjectIds = (e: LedgerEvent, role: string) => e.subjects.filter((s) => s.role === role).map((s) => s.id);
const dateOf = (e: LedgerEvent) => (e.occurred_at ?? "").slice(0, 10);

/** Pounds of pay weight for a group of head: the stated total, or the stated average times head. */
export function payTotal(avg: Quantity | undefined, total: Quantity | undefined, head: number): number | null {
  const t = toLb(total);
  if (t !== null) return t;
  const a = toLb(avg);
  return a !== null ? a * head : null;
}

/** Split of a receipt across pens: the stated split, or every head into the one pen named. */
export function receiptPens(data: ReceiveData, penIds: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  if (data.pen_split?.length) {
    for (const s of data.pen_split) out[s.pen_id] = (out[s.pen_id] ?? 0) + s.head;
    return out;
  }
  if (penIds[0]) out[penIds[0]] = data.head_received;
  return out;
}

/** Reduce a lot's feeding events (oldest first) to its counted inventory. Undated events are skipped. */
export function reduceLot(events: LedgerEvent[], today: string): LotState | null {
  const state: LotState = {
    received: 0, expected: null, short: 0, in_on: null, receipts: [], outs: [], moves: [], ledger: [], owners: [], owners_total: null,
    target_days: null, origin: null, head: 0, pens: {}, out: { shipped: 0, died: 0, transferred: 0, missing: 0 },
  };
  const sorted = [...events].filter((e) => e.occurred_at).sort((a, b) => dateOf(a).localeCompare(dateOf(b)));
  for (const e of sorted) {
    const on = dateOf(e);
    if (e.type === "feeding.receive") {
      const d = e.data as ReceiveData;
      const pens = receiptPens(d, subjectIds(e, "pen"));
      const pay = payTotal(d.pay_weight, d.pay_weight_total, d.head_received);
      state.receipts.push({
        event_id: e.id, on, head: d.head_received, expected: d.head_expected ?? null, pens, pay_lb_total: pay,
        pay_avg_lb: pay !== null && d.head_received > 0 ? pay / d.head_received : null, seller_id: subjectIds(e, "seller")[0] ?? null,
        shortage_reason: d.shortage_reason ?? null, identified: subjectIds(e, "animal").length,
      });
      state.received += d.head_received;
      if (d.head_expected !== undefined) state.expected = (state.expected ?? 0) + d.head_expected;
      state.short += Math.max((d.head_expected ?? d.head_received) - d.head_received, 0);
      state.in_on = state.in_on && state.in_on < on ? state.in_on : on;
      if (d.owners?.length) {
        state.owners = d.owners.map((o) => ({ contact_id: o.contact_id, share: o.share }));
        state.owners_total = d.owners.reduce((n, o) => n + o.share, 0);
      }
      if (d.target_days) state.target_days = d.target_days;
      if (d.origin) state.origin = d.origin;
      for (const [pen, head] of Object.entries(pens)) state.ledger.push({ on, pen_id: pen, delta: head, kind: "in", event_id: e.id });
    } else if (e.type === "feeding.lot_move") {
      const d = e.data as LotMoveData;
      const from = subjectIds(e, "from")[0] ?? null;
      const to = subjectIds(e, "to")[0] ?? null;
      state.moves.push({ event_id: e.id, on, head: d.head, from, to });
      state.ledger.push({ on, pen_id: from, delta: -d.head, kind: "move", event_id: e.id });
      state.ledger.push({ on, pen_id: to, delta: d.head, kind: "move", event_id: e.id });
    } else if (e.type === "feeding.head_out") {
      const d = e.data as HeadOutData;
      let pen = subjectIds(e, "pen")[0] ?? null;
      if (!pen) {
        // Unnamed pen: take them from wherever most of the lot was that day.
        const byPen = headByPen(state.ledger, on);
        pen = Object.entries(byPen).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
      }
      state.outs.push({ event_id: e.id, on, head: d.head, reason: d.reason, pen_id: pen, pay_lb_total: d.reason === "shipped" ? payTotal(d.pay_weight, d.pay_weight_total, d.head) : null });
      state.out[d.reason] = (state.out[d.reason] ?? 0) + d.head;
      state.ledger.push({ on, pen_id: pen, delta: -d.head, kind: "out", reason: d.reason, event_id: e.id });
    }
  }
  if (!state.receipts.length && !state.outs.length && !state.moves.length) return null;
  state.head = headOn(state.ledger, today);
  state.pens = headByPen(state.ledger, today);
  return state;
}

// ---------------------------------------------------------------- pen feeding state

export interface PenState {
  ration: { version_id: string; name: string | null; since: string } | null;
  last_call: { on: string; event_id: string; amount_lb: number | null; per_head_lb: number | null; bunk: string | null } | null;
  last_delivery: { on: string; event_id: string; amount_lb: number } | null;
  uses_calls: boolean;
  fed_since: string | null;
  /** The pen has held a feeding lot at some point. */
  has_lots: boolean;
}

export function reducePen(events: LedgerEvent[]): PenState | null {
  if (!events.length) return null;
  const state: PenState = { ration: null, last_call: null, last_delivery: null, uses_calls: false, fed_since: null, has_lots: false };
  const sorted = [...events].sort((a, b) => (a.occurred_at ?? "").localeCompare(b.occurred_at ?? ""));
  for (const e of sorted) {
    const on = dateOf(e);
    if (e.type === "feeding.call") {
      const d = e.data as CallData;
      state.uses_calls = true;
      state.last_call = { on, event_id: e.id, amount_lb: toLb(d.amount), per_head_lb: toLb(d.per_head), bunk: d.bunk ?? null };
      if (d.ration_version_id && state.ration?.version_id !== d.ration_version_id) state.ration = { version_id: d.ration_version_id, name: d.ration ?? null, since: on };
    } else if (e.type === "feeding.delivery") {
      const d = e.data as DeliveryData;
      const lb = toLb(d.amount) ?? 0;
      if (lb > 0) {
        state.last_delivery = { on, event_id: e.id, amount_lb: lb };
        state.fed_since = state.fed_since ?? on;
      }
      if (d.ration_version_id && state.ration?.version_id !== d.ration_version_id) state.ration = { version_id: d.ration_version_id, name: d.ration ?? null, since: on };
    } else {
      state.has_lots = true;
    }
  }
  return state;
}

// ---------------------------------------------------------------- an animal received into a lot

export interface AnimalFeedState { lot_id: string | null; received_on: string; event_id: string; out: { on: string; reason: string } | null }

export function reduceAnimal(events: LedgerEvent[]): AnimalFeedState | null {
  let state: AnimalFeedState | null = null;
  const sorted = [...events].sort((a, b) => (a.occurred_at ?? "").localeCompare(b.occurred_at ?? ""));
  for (const e of sorted) {
    const lot = subjectIds(e, "lot")[0] ?? null;
    if (e.type === "feeding.receive") state = { lot_id: lot, received_on: dateOf(e), event_id: e.id, out: null };
    if (e.type === "feeding.head_out" && state) state.out = { on: dateOf(e), reason: (e.data as HeadOutData).reason };
  }
  return state;
}
